import * as React from 'react';
import { createPortal } from 'react-dom';
import { Trans, useLingui } from '@lingui/react/macro';
import Icon from 'components/Icon';
import { endSession, userSession } from 'state/session';
import { showToast } from 'state/toast';
import { DgaRequest, getDGA } from 'utils/ui/dga';
import { user } from 'utils/auth/user';
import { analyticsState } from 'state/analytics';
import { brandSchema } from 'data/schema';
import { errorCollection } from 'config/ui/error-collection';
import Button from 'components/Button';
import { useWebComponent } from 'components/WebComponentRoot';
import { ApiError } from 'utils/errors';
import { classNames } from 'utils/ui/css';
import { getCurrentDealerId } from 'utils/dgaUtils';
import { updateVehicle } from 'state/session/vehicleRegistry';
import { tenantConfig } from 'utils/tenant';

type TDgaModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

const DgaModal: React.FC<TDgaModalProps> = ({ isOpen, setIsOpen }) => {
  const [isCreating, setIsCreating] = React.useState(false);
  const [cameraFlight, setCameraFlight] = React.useState(false);
  const { t } = useLingui();
  const { rootElement } = useWebComponent();

  const session = userSession().useState();
  const brand = brandSchema.parse(session?.brand || user.brand);
  const currentDealerId = getCurrentDealerId(brand);

  const endSessionAndCloseModal = () => {
    try {
      endSession();
    } catch (error) {
      console.error('Failed to end session:', error);
    } finally {
      closeModal();
    }
  };

  const closeModal = () => setIsOpen(false);

  const createDGA = async () => {
    setIsCreating(true);
    try {
      const dgaLinkResponse = await getDGA({
        marketingCode: session.vinOrMarketingCode as string,
        locale: session.locale,
        market: session.market as string,
        dealerId: currentDealerId,
        brand,
        renderCameraFlight: cameraFlight,
      } as DgaRequest);
      updateVehicle({ dgaLink: dgaLinkResponse?.url as string });
      showToast({
        title: t`Digital Giveaway created successfully`,
        type: 'success',
        notificationId: 'SUCCESS_DGA_CREATE',
      });
      analyticsState().setValues({ dealerToolUsed: 'createDGA', dgaGeneratedAt: 'end' });
    } catch (error) {
      showToast({
        title: errorCollection[(error as ApiError).code].title(),
        message: errorCollection[(error as ApiError).code].message(),
        type: 'error',
        notificationId: errorCollection[(error as ApiError).code].notificationId,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateDgaAndEnd = async () => {
    await createDGA();
    endSession();
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/50" />

      {/* Modal content */}
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="w-auto max-w-[570px] rounded-xl bg-white p-6 text-black shadow-xl">
          {/* Close button */}
          <div className="mb-4 flex justify-end">
            <button
              onClick={closeModal}
              className="text-teal-700 hover:text-teal-600-10"
              data-testid="dga-close-button"
            >
              <Icon className="ex3d-icon--close size-5 bg-teal" />
            </button>
          </div>

          {/* Title & subtitle */}
          <h2 className="mb-2 text-center text-xl font-bold">
            <Trans>Your session is about to end</Trans>
          </h2>
          <p className="mb-6 text-center text-sm">
            <Trans>Do you want to create a Digital Giveaway for your customer?</Trans>
          </p>

          {/* Checkbox - Show based on tenant configuration */}
          {tenantConfig().isAllowed('dgaCamFlight') && (
            <div className="mb-6 flex items-start gap-2">
              <input
                id="cameraFlight"
                type="checkbox"
                checked={cameraFlight}
                onChange={() => setCameraFlight(!cameraFlight)}
                className="size-5 cursor-pointer rounded border-2 border-gray-400"
                data-testid="dga-camera-flight-checkbox"
              />
              <label htmlFor="cameraFlight" className="text-sm leading-tight">
                <Trans>Add camera flight</Trans>
                <br />
                <span className="text-xs text-gray-400">
                  <Trans>(this might take several minutes)</Trans>
                </span>
              </label>
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-4 flex flex-wrap justify-end gap-3 sm:flex-nowrap">
            <Button
              text={t`Cancel`}
              data-testid="dga-cancel-button"
              onClick={closeModal}
              className={classNames(
                'rounded-full border border-teal bg-white px-4 py-1 text-sm font-normal text-teal transition',
                isCreating ? 'cursor-not-allowed' : 'hover:bg-gray-100'
              )}
              disabled={isCreating}
            />
            <Button
              text={t`End session`}
              data-testid="session-end-modal-button"
              onClick={endSessionAndCloseModal}
              className={classNames(
                'rounded-full border border-teal bg-white px-4 py-1 text-sm font-normal text-teal transition',
                isCreating ? 'cursor-not-allowed' : 'hover:bg-gray-100'
              )}
              disabled={isCreating}
            />
            <Button
              text={t`End session & create DGA`}
              data-testid="end-session-create-dga-button"
              onClick={handleCreateDgaAndEnd}
              icon={isCreating ? 'ex3d-icon--loader' : 'ex3d-icon--gift'}
              className={classNames(
                'rounded-full text-sm text-white transition bg-teal',
                isCreating ? 'cursor-not-allowed px-5 py-2.5' : 'px-4 py-1.5'
              )}
              svgClassName={classNames('bg-white', isCreating ? 'size-5' : 'size-7')}
              disabled={isCreating}
            />
          </div>
        </div>
      </div>
    </>
  );

  // Portal to shadow root (if available) or fallback to document.body
  return createPortal(modalContent, rootElement || document.body);
};

export default DgaModal;
